import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Chip,
  Paper,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Drawer,
  AppBar,
  Toolbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PurpleTaskModal from './PurpleTaskModal';

const statusColors = {
  "Code Added": "#e8f5e9",
  "Code Accepted": "#f0faff",
  "Code Dismissed": "#ffecec",
  "Code Unattended": "#fff9e6",
  "Existing Code Refreshed": '#e0f7fa',
  "Existing Code UnRefreshed": "#f0faff",
  "No Code Added": "#e0e0e0",
  "Code Follow Up": "#ebe3f3",
  "Recapture Accepted": "#f0faff",
  "Recapture Unattended": "#fff9e6",
  "Recapture Dismissed": "#ffecec",
  "Recapture Follow Up": "#ebe3f3",
  "Allymar Accepted": "#f0faff",
  "Allymar Unattended": "#fff9e6",
  "Allymar Dismissed": "#ffecec",
  "Allymar Follow Up": "#ebe3f3",
};

const chipStyles = {
  default: { backgroundColor: '#333', color: '#fff' },
  warning: { backgroundColor: '#cc7000', color: '#fff' }
};

// Helper function to get matching code statuses based on the selected metric
const getMatchingCodeStatuses = (metric) => {
  const statusMapping = {
    'accepted_codes': ['Code Accepted', 'Recapture Accepted', 'Allymar Accepted'],
    'dismissed_codes': ['Code Dismissed', 'Recapture Dismissed', 'Allymar Dismissed'],
    'unattended_codes': ['Code Unattended', 'Recapture Unattended', 'Allymar Unattended'],
    'existing_unrefreshed_codes': ['Existing Code UnRefreshed'],
    'resolved': [],
    'doctor_codes': [], // Will need to filter by code_source
    'coder_codes': [], // Will need to filter by code_source
    'new_codes': ['Code Added'],
    'visits_with_new_codes': [], // Special case - show all codes for visits that have any new codes
    'visits_without_new_codes': ['No Code Added', 'Existing Code Refreshed', 'Existing Code UnRefreshed'],
    'patients_visits': [] // Show all codes for all visits
  };
  
  return statusMapping[metric] || [];
};

// Helper function to check if an action should be included based on the metric
const shouldIncludeAction = (action, metric) => {
  const matchingStatuses = getMatchingCodeStatuses(metric);
  
  // Special cases for doctor_codes and coder_codes - filter by code_source
  if (metric === 'doctor_codes') {
    return action.code_source === 'Doctor';
  }
  
  if (metric === 'coder_codes') {
    return action.code_source === 'Coder';
  }
  
  // For patients_visits, show all actions
  if (metric === 'patients_visits') {
    return true;
  }
  
  // For visits_with_new_codes, show all actions (the backend already filters visits)
  if (metric === 'visits_with_new_codes') {
    return true;
  }
  
  // For other metrics, filter by code_status
  if (matchingStatuses.length === 0) {
    return true; // If no specific statuses defined, show all
  }
  
  return matchingStatuses.includes(action.code_status);
};

const formatApolloMessage = (message, handleDeleteMessage) => {
  const info = (
    <>
      <div
        style={{
          backgroundColor: '#A1E7A4',
          padding: '8px 12px',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          color: '#333',
          position: 'relative',
          fontWeight: 'bold',
          textAlign: 'center',
        }}
      >
        Apollo Message ({message.DiagnosisCode}/{message.HCC})
      </div>
      <div style={{ padding: '12px 18px' }}>
        <div><strong>{message.created_by}:</strong> {message.Description}</div>
        <div
          style={{
            display: 'flex',
          }}
        >
          <div style={{ color: 'grey', marginLeft: 'auto', fontSize: '12px' }}>
            {new Date(message.created_at).toLocaleString()}
          </div>
        </div>
        {message.feedback_by && (
          <div
            style={{
              display: 'flex',
              marginTop: '12px',
              padding: '10px',
              backgroundColor: '#FFECB3',
              borderRadius: '8px',
              border: '1px solid #FFB74D',
            }}
          >
            <div><strong>{message.feedback_by}:</strong> {message.feedback}</div>
            <div style={{ color: 'grey', marginLeft: 'auto', fontSize: '12px' }}>
              {new Date(message.feedback_at).toLocaleString()}
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div>
      <Box
        sx={{
          backgroundColor: '#E5F1B5',
          borderRadius: '8px',
          display: 'inline-block',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          boxShadow: '2px 4px 8px rgba(0, 0, 0, 0.15)',
          fontFamily: 'Arial, sans-serif',
          color: '#333',
          overflow: 'hidden',
          width: { xs: '100%', sm: '400px' },
          marginTop: '15px'
        }}
      >
        {info}
      </Box>
      <br />
    </div>
  );
};

const PurpleVisitDetails = ({ 
  open, 
  onClose, 
  visitDetails, 
  selectedTask, 
  username, 
  token,
  onRefresh
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [openModal, setOpenModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  
  // Refs for scrolling to patients and scroll container
  const patientRefs = useRef({});
  const scrollContainerRef = useRef(null);

  // Move all hooks before any conditional returns
  // Group the data by patient and encounter date
  const grouped = visitDetails ? visitDetails.reduce((acc, row) => {
    const formattedDate = row.encounter_date;
    const key = `${row.patient_id}-${formattedDate}`;
    if (!acc[key]) acc[key] = { patient_id: row.patient_id, encounter_date: formattedDate, actions: [] };
    acc[key].actions.push(row);
    return acc;
  }, {}) : {};

  const groupedArray = Object.values(grouped).map(visit => {
    // Filter actions based on the selected metric
    let filteredActions = visit.actions;
    
    if (selectedTask?.metric) {
      filteredActions = visit.actions.filter(action => 
        shouldIncludeAction(action, selectedTask.metric)
      );
    }
    
    // If no matching actions after filtering, but we still have the original "No Code Added" logic
    const hasOtherActions = filteredActions.some(action => action.code_status !== 'No Code Added');
    if (hasOtherActions) {
      filteredActions = filteredActions.filter(action => action.code_status !== 'No Code Added');
    }
    
    return { ...visit, actions: filteredActions };
  }).filter(visit => visit.actions.length > 0); // Remove visits with no matching actions

  // Create patient navigation list
  const patientNavList = groupedArray.map((visit, index) => ({
    patient_id: visit.patient_id,
    encounter_date: visit.encounter_date,
    index,
    actionCount: visit.actions.length,
    key: `${visit.patient_id}-${visit.encounter_date}`
  }));

  // Set first patient as selected on mount - moved before conditional return
  useEffect(() => {
    if (patientNavList.length > 0 && !selectedPatient) {
      setSelectedPatient(patientNavList[0].key);
    }
  }, [patientNavList, selectedPatient]);

  // Add scroll listener to update selected patient based on scroll position
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || patientNavList.length === 0) return;

    const handleScroll = () => {
      const scrollTop = scrollContainer.scrollTop;
      const containerHeight = scrollContainer.clientHeight;
      const midPoint = scrollTop + containerHeight / 3; // Use top third of viewport as trigger point

      // Find which patient section is currently in view
      let currentPatient = null;
      let minDistance = Infinity;

      patientNavList.forEach(patient => {
        const element = patientRefs.current[patient.key];
        if (element) {
          const elementTop = element.offsetTop;
          const elementBottom = elementTop + element.offsetHeight;
          
          // Check if the element is in the viewport area we care about
          if (elementTop <= midPoint && elementBottom >= scrollTop) {
            const distance = Math.abs(elementTop - midPoint);
            if (distance < minDistance) {
              minDistance = distance;
              currentPatient = patient.key;
            }
          }
        }
      });

      // Update selected patient if it changed
      if (currentPatient && currentPatient !== selectedPatient) {
        setSelectedPatient(currentPatient);
      }
    };

    // Add scroll event listener with throttling
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    scrollContainer.addEventListener('scroll', throttledHandleScroll);
    
    // Initial check
    handleScroll();

    return () => {
      scrollContainer.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [patientNavList, selectedPatient]);

  const handleDeleteMessage = async (message) => {
    // Implementation for deleting Apollo messages
    console.log('Delete message:', message);
    // Add your delete logic here
  };

  // Replace feedback handlers with TaskModal handlers
  const handleOpenModal = (action) => {
    setSelectedAction(action);
    setOpenModal(true);
  };
  
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedAction(null);
  };

  const handleSuccess = () => {
    // Call the refresh function passed from parent
    if (onRefresh) {
      onRefresh();
    }
  };

  // Handle patient selection from sidebar
  const handlePatientSelect = (patientKey) => {
    setSelectedPatient(patientKey);
    if (patientRefs.current[patientKey]) {
      patientRefs.current[patientKey].scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Now place the conditional return after all hooks
  if (!visitDetails || visitDetails.length === 0) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth 
        fullScreen={fullScreen}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <div>
              <Typography variant="h6">
                Visit Details – {selectedTask?.metric?.replaceAll('_', ' ')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedTask?.department} / {selectedTask?.provider}
              </Typography>
            </div>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box textAlign="center" py={4}>
            <Typography color="textSecondary">
              No visit details found for this metric.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Sidebar content - more condensed version
  const sidebarContent = (
    <Box sx={{ width: 220, height: '100%', bgcolor: 'background.paper' }}>
      <Box sx={{ p: 1.5, bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Patients ({patientNavList.length})
        </Typography>
      </Box>
      
      <List sx={{ p: 0, '& .MuiListItem-root': { py: 0 } }}>
        {patientNavList.map((patient, index) => (
          <React.Fragment key={patient.key}>
            <ListItem disablePadding>
              <ListItemButton 
                selected={selectedPatient === patient.key}
                onClick={() => handlePatientSelect(patient.key)}
                sx={{
                  py: 1,
                  px: 1.5,
                  minHeight: 'auto',
                  '&.Mui-selected': {
                    backgroundColor: '#e8f4fd', // Changed to a much lighter pastel blue
                    color: '#1565c0', // Darker blue text for better contrast
                    '&:hover': {
                      backgroundColor: '#d1e7dd', // Light pastel green on hover
                    }
                  }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 0.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ mr: 0.5, fontSize: 16 }} />
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                      {patient.patient_id}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 0.5, fontSize: 14, opacity: 0.7 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      {patient.encounter_date}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {patient.actionCount} action{patient.actionCount !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </ListItemButton>
            </ListItem>
            {index < patientNavList.length - 1 && <Divider sx={{ my: 0 }} />}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  const drawer = (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? mobileOpen : true}
      onClose={handleDrawerToggle}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        '& .MuiDrawer-paper': {
          position: 'relative',
          whiteSpace: 'nowrap',
          width: 220, // Reduced from 280
          height: '100%',
          boxSizing: 'border-box',
        },
      }}
    >
      {sidebarContent}
    </Drawer>
  );

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="xl" 
        fullWidth 
        fullScreen={fullScreen}
        sx={{
          '& .MuiDialog-paper': {
            height: fullScreen ? '100%' : '90vh',
            maxHeight: fullScreen ? '100%' : '90vh',
          }
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <AppBar position="static" color="default" elevation={1}>
            <Toolbar>
              {isMobile && (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 2 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ mb: 0.5 }}>
                  Visit Details – {selectedTask?.metric?.replaceAll('_', ' ')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  {selectedTask?.department} / {selectedTask?.provider}
                </Typography>
              </Box>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
        </DialogTitle>
        
        <Box sx={{ display: 'flex', height: 'calc(100% - 64px)' }}>
          {!isMobile && drawer}
          
          <Box 
            component="main" 
            sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              flexDirection: 'column',
              width: isMobile ? '100%' : `calc(100% - 220px)` // Updated to match new sidebar width
            }}
          >
            <DialogContent 
              dividers 
              ref={scrollContainerRef} // Add ref to scroll container
              sx={{ 
                padding: { xs: 1, sm: 2 },
                flexGrow: 1,
                overflow: 'auto'
              }}
            >
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                {groupedArray.map((visit, index) => (
                  <Grid item xs={12} key={index}>
                    <Paper 
                      elevation={2} 
                      sx={{ 
                        p: { xs: 1, sm: 2 }, 
                        backgroundColor: selectedPatient === `${visit.patient_id}-${visit.encounter_date}` 
                          ? '#f0f8ff'
                          : '#e9e9e9',
                        border: selectedPatient === `${visit.patient_id}-${visit.encounter_date}` 
                          ? '2px solid #87ceeb'
                          : '1px solid transparent',
                        transition: 'all 0.3s ease'
                      }}
                      ref={el => patientRefs.current[`${visit.patient_id}-${visit.encounter_date}`] = el}
                    >
                      <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ mb: 1 }}>
                        🧍 Patient ID: <strong>{visit.patient_id}</strong>
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                        📅 Encounter Date: <strong>{visit.encounter_date}</strong>
                      </Typography>

                      <Grid container spacing={2}>
                        {visit.actions.map((action, idx) => (
                          <Grid item xs={12} lg={6} key={idx}>
                            <Card 
                              sx={{ 
                                backgroundColor: statusColors[action.code_status] || '#f0f0f0',
                                height: '100%'
                              }} 
                              variant="outlined"
                            >
                              <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center', 
                                  mb: 1,
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  gap: { xs: 1, sm: 0 }
                                }}>
                                  <Chip
                                    label={action.code_status}
                                    size={isMobile ? "small" : "medium"}
                                    sx={
                                      action.code_status === 'Code Unattended' || 
                                      action.code_status === 'Existing Code UnRefreshed' || 
                                      action.code_status === 'Recapture Accepted' || 
                                      action.code_status === 'Code Accepted' || 
                                      action.code_status === 'Recapture Unattended' || 
                                      action.code_status === 'Allymar Accepted' || 
                                      action.code_status === 'Allymar Unattended'
                                        ? chipStyles.warning
                                        : chipStyles.default
                                    }
                                  />
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                      color: '#555',
                                      borderColor: '#ccc',
                                      textTransform: 'none',
                                      fontWeight: 400,
                                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                      '&:hover': {
                                        borderColor: '#999',
                                        backgroundColor: '#f5f5f5'
                                      }
                                    }}
                                    onClick={() => handleOpenModal(action)}
                                  >
                                    + Feedback
                                  </Button>
                                </Box>
                                
                                <Box sx={{ 
                                  display: 'flex', 
                                  flexDirection: 'column', 
                                  gap: 0.5 
                                }}>
                                  <Typography variant="body2">
                                    <strong>Code Source:</strong> {action.code_source}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>HCC Code:</strong> {action.hcc_code || 'N/A'}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Diagnosis Code:</strong> {action.diagnosis_code || 'N/A'}
                                  </Typography>
                                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                    <strong>Diagnosis Desc:</strong> {action.diagnosis_description || 'N/A'}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Last Assessed:</strong> {action.details?.recent_dt || 'Never'}
                                  </Typography>
                                </Box>

                                {action.messages && action.messages.map((msg, msgIdx) => (
                                  <div key={msgIdx}>
                                    {formatApolloMessage(msg, handleDeleteMessage)}
                                  </div>
                                ))}

                                {Array.isArray(action.details) && action.details.length > 0 && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                      Coder Communications
                                    </Typography>
                                    <Box sx={{ pl: 1, borderLeft: '3px solid #aaa' }}>
                                      {action.details.map((detail, dIdx) => (
                                        <Box key={dIdx} sx={{ mb: 1, p: 1, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                                          <Typography variant="caption" display="block">
                                            <strong>Detected At:</strong> {detail.detected_at}
                                          </Typography>
                                          <Typography variant="caption" display="block">
                                            <strong>Detected By:</strong> {detail.detected_by}
                                          </Typography>
                                          <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                                            <strong>Message:</strong> {detail.message_content}
                                          </Typography>
                                          {detail.status && (
                                            <Typography variant="caption" display="block">
                                              <strong>Status:</strong> {detail.status}
                                            </Typography>
                                          )}
                                          {detail.message_type && (
                                            <Typography variant="caption" display="block">
                                              <strong>Channel:</strong> {detail.message_type}
                                            </Typography>
                                          )}
                                          {detail.feedback && (
                                            <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                                              <strong>{detail.feedback_by} ({detail.feedback_at}):</strong> {detail.feedback}
                                            </Typography>
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </DialogContent>
            
            <DialogActions sx={{ p: { xs: 1, sm: 2 } }}>
              <Button onClick={onClose} variant="contained">
                Close
              </Button>
            </DialogActions>
          </Box>
        </Box>

        {/* Mobile drawer */}
        {isMobile && (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: 220, // Updated width for mobile too
              },
            }}
          >
            {sidebarContent}
          </Drawer>
        )}
      </Dialog>

      {openModal && (
        <PurpleTaskModal
          open={openModal}
          onClose={handleCloseModal}
          username={username}
          token={token}
          action={selectedAction}
          onSuccess={handleSuccess}
        />
      )}
    </>
  );
};

export default PurpleVisitDetails;